<template>
  <div class="fds-rich-text-editor__menubar">
    <!-- undo/redo -->
    <menu-bar-group>
      <menu-bar-button
        label="Undo"
        :disabled="!editor?.can().chain().focus().undo().run()"
        @click.prevent="editor?.chain().focus().undo().run()"
      >
        <icon-arrow-back-up :stroke="stroke" />
      </menu-bar-button>
      <menu-bar-button
        label="Redo"
        :disabled="!editor?.can().chain().focus().redo().run()"
        @click.prevent="editor?.chain().focus().redo().run()"
      >
        <icon-arrow-forward-up :stroke="stroke" />
      </menu-bar-button>
    </menu-bar-group>

    <!-- formatting -->
    <menu-bar-group>
      <menu-bar-button
        label="Bold"
        :is-active="editor?.isActive('bold')"
        @click.prevent="editor?.chain().focus().toggleBold().run()"
      >
        <icon-bold :stroke="stroke" />
      </menu-bar-button>

      <menu-bar-button
        label="Italic"
        :is-active="editor?.isActive('italic')"
        @click.prevent="editor?.chain().focus().toggleItalic() .run()"
      >
        <icon-italic :stroke="stroke" />
      </menu-bar-button>

      <menu-bar-button
        label="Underline"
        :is-active="editor?.isActive('underline')"
        @click.prevent="editor?.chain().focus().toggleUnderline().run()"
      >
        <icon-underline :stroke="stroke" />
      </menu-bar-button>

      <menu-bar-button
        label="Strikethrough"
        :is-active="editor?.isActive('strike')"
        @click.prevent="editor?.chain().focus().toggleStrike().run()"
      >
        <icon-strikethrough :stroke="stroke" />
      </menu-bar-button>
    </menu-bar-group>

    <!-- alignment -->
    <menu-bar-group>
      <menu-bar-button
        label="Left align"
        :is-active="editor?.isActive({ textAlign: 'left' })"
        @click.prevent="editor?.chain().focus().setTextAlign('left').run()"
      >
        <icon-align-left :stroke="stroke" />
      </menu-bar-button>

      <menu-bar-button
        label="Center align"
        :is-active="editor?.isActive({ textAlign: 'center' })"
        @click.prevent="editor?.chain().focus().setTextAlign('center').run()"
      >
        <icon-align-center :stroke="stroke" />
      </menu-bar-button>

      <menu-bar-button
        label="Right align"
        :is-active="editor?.isActive({ textAlign: 'right' })"
        @click.prevent="editor?.chain().focus().setTextAlign('right').run()"
      >
        <icon-align-right :stroke="stroke" />
      </menu-bar-button>

      <menu-bar-button
        label="Justify"
        :is-active="editor?.isActive({ textAlign: 'justify' })"
        @click.prevent="editor?.chain().focus().setTextAlign('justify').run()"
      >
        <icon-align-justified :stroke="stroke" />
      </menu-bar-button>
    </menu-bar-group>

    <!-- lists -->
    <menu-bar-group>
      <menu-bar-button
        label="Bullet list"
        :is-active="editor?.isActive('bulletList')"
        @click.prevent="editor?.chain().focus().toggleBulletList().run()"
      >
        <icon-list :stroke="stroke" />
      </menu-bar-button>

      <menu-bar-button
        label="Numbered list"
        :is-active="editor?.isActive('orderedList')"
        @click.prevent="editor?.chain().focus().toggleOrderedList().run()"
      >
        <icon-list-numbers :stroke="stroke" />
      </menu-bar-button>
    </menu-bar-group>

    <!-- subscript and superscript -->
    <menu-bar-group>
      <menu-bar-button
        label="Subscript"
        :is-active="editor?.isActive('subscript')"
        @click.prevent="editor?.chain().focus().unsetSuperscript().toggleSubscript().run()"
      >
        <icon-subscript :stroke="stroke" />
      </menu-bar-button>

      <menu-bar-button
        label="Superscript"
        :is-active="editor?.isActive('superscript')"
        @click.prevent="editor?.chain().focus().unsetSubscript().toggleSuperscript().run()"
      >
        <icon-superscript :stroke="stroke" />
      </menu-bar-button>
    </menu-bar-group>

    <!-- table -->
    <menu-bar-group>
      <menu-bar-button
        label="Create table"
        @click="editor.chain().focus().insertTable({rows: 2, cols: 2, withHeaderRow: true}).run()"
      >
        <icon-table :stroke="stroke" />
      </menu-bar-button>
      <menu-bar-button
        label="Add row"
        :disabled="disableTableControls"
        @click="editor.chain().focus().addRowAfter().run()"
      >
        <icon-row-insert-top :stroke="stroke" />
      </menu-bar-button>
      <menu-bar-button
        label="Remove row"
        :disabled="disableTableControls"
        @click="editor.chain().focus().deleteRow().run()"
      >
        <icon-row-remove :stroke="stroke" />
      </menu-bar-button>
      <menu-bar-button
        label="Add column"
        :disabled="disableTableControls"
        @click="editor.chain().focus().addColumnAfter().run()"
      >
        <icon-column-insert-left :stroke="stroke" />
      </menu-bar-button>
      <menu-bar-button
        label="Remove column"
        :disabled="disableTableControls"
        @click="editor.chain().focus().deleteColumn().run()"
      >
        <icon-column-remove :stroke="stroke" />
      </menu-bar-button>
      <menu-bar-button
        label="Toggle header row"
        :disabled="disableTableControls"
        @click="editor.chain().focus().toggleHeaderRow().run()"
      >
        <icon-table-row :stroke="stroke" />
      </menu-bar-button>
      <menu-bar-button
        label="Toggle header column"
        :disabled="disableTableControls"
        @click="editor.chain().focus().toggleHeaderColumn().run()"
      >
        <icon-table-column :stroke="stroke" />
      </menu-bar-button>
      <menu-bar-button
        label="Remove table"
        :disabled="disableTableControls"
        @click="editor.chain().focus().deleteTable().run()"
      >
        <icon-table-minus :stroke="stroke" />
      </menu-bar-button>
    </menu-bar-group>
  </div>
</template>

<script setup>
import {
  IconAlignCenter,
  IconAlignJustified,
  IconAlignLeft,
  IconAlignRight,
  IconArrowBackUp,
  IconArrowForwardUp,
  IconBold,
  IconColumnInsertLeft,
  IconColumnRemove,
  IconItalic,
  IconList,
  IconListNumbers,
  IconRowInsertTop,
  IconRowRemove,
  IconStrikethrough,
  IconSuperscript,
  IconSubscript,
  IconTable,
  IconTableColumn,
  IconTableMinus,
  IconTableRow,
  IconUnderline,
} from "@tabler/icons-vue";
import { Editor } from "@tiptap/core";
import { computed } from "vue";
import MenuBarGroup from "./MenuBarGroup.vue";
import MenuBarButton from "./MenuBarButton.vue";

const stroke = 2;

const props = defineProps({ editor: { type: Editor, required: true } });

const disableTableControls = computed(() => !props.editor.isActive("table"));

</script>
